import AddIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import { Button, FormControlLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Mention, MentionsInput } from 'react-mentions';
import { useDispatch } from 'react-redux';
import CheckBoxGroupBoolean from 'src/app/common/components/CheckBoxGroupBoolean';
import AgentSegmentField from './AgentSegmentField';
import FileUploader from 'src/app/common/components/FileUploader';
import Form from 'src/app/common/components/Form';
import Tinymce from 'src/app/common/components/Tinymce-storage-blob';
import { MANDATORY_FIELD_ERROR_TEXT } from 'src/app/common/constants';
import { createBlob, getBlob } from 'src/app/common/network';
import { useCommonStyles } from 'src/app/common/styles/common-styles';
import { FormMode } from 'src/app/common/types';
import { fileUpload } from 'src/app/common/utils';
import {
  PROSPECT_REFERRAL_BASE_PATH,
  PROSPECT_REFERRAL_INITIAL_ITEM_LEAD_SOURCES,
} from '../../../constants';
import { EPublishStatus, EReceiverTypeAgent } from '../../../enum/prospect-referral.enum';
import { stylesProspectReferral } from '../../../styles/stylesProspectReferral';
import {
  AgentSegmentOptions,
  MentionList,
  ProspectReferralFormState,
  ReferralFormOption,
} from '../../../types/prospect-referral-types';

const useStyles = stylesProspectReferral;

interface ProspectReferralDetailFormProps {
  initialValues: ProspectReferralFormState;
  onSubmit: (formData: ProspectReferralFormState, status: string) => void;
  history: any;
  formMode: FormMode;
  formLoading: boolean;
  disabled: boolean;
}

const ProspectReferralDetailForm: React.FC<ProspectReferralDetailFormProps> = (props) => {
  const { classes } = useStyles();
  const commonClasses = useCommonStyles().classes;

  const [form] = Form.useForm();
  const [leadSources, setLeadSources] = useState<Array<{ _id: string; sourceId: string; sourceName: string }>>(PROSPECT_REFERRAL_INITIAL_ITEM_LEAD_SOURCES);

  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const DEFAULT_REQUIRED_RULES = [{ required: true, message: Translation('component.form-required-text') }];

  const TRIMMED_REQUIRED_RULES = [
    {
      validator: (_: any, value: any) => {
        if (!value || !value.trim()) {
          return Promise.reject(new Error(Translation('component.form-required-text')));
        }
        return Promise.resolve();
      },
    },
  ];

  const TINYMCE_REQUIRED_RULES = [
    {
      validator: (_: any, value: any) => {
        const text = value ? value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim() : '';
        if (!text) {
          return Promise.reject(new Error(Translation('component.form-required-text')));
        }
        return Promise.resolve();
      },
    },
  ];

  const AGENT_SEGMENT_REQUIRED_RULES = [
    { required: true, message: MANDATORY_FIELD_ERROR_TEXT },
    () => ({
      async validator(_: any, value: any) {
        if (value) {
          if (
            !value[EReceiverTypeAgent.isTraditionalAgents] &&
            !value[EReceiverTypeAgent.isGroupOfUsers] &&
            !value[EReceiverTypeAgent.isPruventure]
          ) {
            throw new Error(Translation('component.form-required-text'));
          }
          if (value[EReceiverTypeAgent.isGroupOfUsers] && !value.userFile) {
            throw new Error(Translation('component.form-required-text'));
          }
        } else {
          throw new Error(Translation('component.form-required-text'));
        }
      },
    }),
  ];

  const onFinish = () => {
    const formData = form.getFieldsValue(true);
    props.onSubmit(formData, formData.status);
  };

  const handleUploadFile = async ({
    file,
    fileName,
    fileType,
  }: {
    file: any;
    fileName: string;
    fileType: string;
  }) => {
    try {
      const createBlobRes = await createBlob(
        { mimeType: fileType, accessLevel: 'anonymous', module: 'prospectReferral' },
        dispatch,
      );
      await fileUpload(createBlobRes.url, file, () => {});
      const blobDetail = await getBlob({ resourceIds: createBlobRes.blobId }, dispatch);
      const result = blobDetail[0];
      if (result) {
        return { id: result.blobId, fileName: fileName, url: result.url, type: fileType };
      }
    } catch (e) {
      console.error('File upload error:', e);
    }
  };

  useEffect(() => {
    form.setFieldsValue(props.initialValues);
  }, [props.initialValues]);

  return (
    <Form
      form={form}
      onFinish={onFinish}
      initialValues={props.initialValues}
      className={classes.container}
      validateTrigger={props.formLoading ? 'onChange' : 'onSubmit'}
    >
      {/* Header */}
      <div className={classes.container}>
        <div className={classes.headerContainer}>
          <div className={classes.rowContainer}>
            <div className={commonClasses.header}>
              {props.formMode === FormMode.EDIT
                ? Translation('prospect-referral.title.edit')
                : Translation('prospect-referral.title')}
            </div>
          </div>
          {props.formMode === FormMode.VIEW ? (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => props.history.push(`${PROSPECT_REFERRAL_BASE_PATH}/edit`)}
            >
              {Translation('component.formTitle.edit')}
            </Button>
          ) : (
            <Button variant="contained" onClick={() => props.history.push(PROSPECT_REFERRAL_BASE_PATH)}>
              {Translation('global.back.btnText')}
            </Button>
          )}
        </div>
      </div>

      {/* Basic Information */}
      <div className={classes.sectionMargin}>
        <div className={classes.divideMargin}>
          <span className={classes.subHeader}>{Translation('prospect-referral.section.basicInformation')}</span>
        </div>

        <Form.Item name="title" label={Translation('prospect-referral.field.titleName')} rules={TRIMMED_REQUIRED_RULES}>
          <TextField
            className={classes.borderError}
            disabled={props.disabled}
            margin="dense"
            variant="outlined"
            fullWidth
            placeholder={Translation('app.input.placeholder.please-enter')}
            inputProps={{ maxLength: 200 }}
          />
        </Form.Item>

        <Typography variant="caption" style={{ color: '#666', marginLeft: 160 }}>
          {Translation('prospect-referral.field.landingPageUrl')}:
        </Typography>
        <div className="pru-form-item" style={{ position: 'relative', width: '100%', marginBottom: 12, paddingLeft: 160 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className={classes.urlPreviewBox} style={{ flex: 1, padding: '10px 14px', minHeight: 40 }}>
              {props?.initialValues?.webflowLink}
            </div>
          </div>
        </div>

        <Form.Item name="bannerImg" label={Translation('prospect-referral.field.bannerImage')} rules={DEFAULT_REQUIRED_RULES}>
          <FileUploader
            color="secondary"
            disabled={props.disabled}
            showAllowText={Translation('prospect-referral.field.bannerImageNote')}
            upload={handleUploadFile}
            allowedFileTypes={['jpg', 'png', 'jpeg']}
            maxFileSize={2}
            showPreview
            needCompressImage={false}
          />
        </Form.Item>
        <br />

        <Form.Item name="introduction" label={Translation('prospect-referral.field.introduction')} rules={TRIMMED_REQUIRED_RULES}>
          <MentionsInput
            disabled={props.disabled}
            singleLine={false}
            className={classes.customMentionInput}
            style={{
              minHeight: '200px',
              border: 'solid 2px #C4C4C4',
              borderRadius: '4px',
              width: '50%',
            }}
          >
            <Mention
              trigger="@"
              data={(search) =>
                MentionList.filter((el) => el.display.toLowerCase().includes(search.toLowerCase()))
              }
              displayTransform={(_id, label) => `@${label}`}
              markup="@{{__id__}}"
              renderSuggestion={(entry, search, highlightedDisplay, index, focused) => (
                <div style={{ padding: '3px 8px' }} key={index} className={focused ? classes.focusedMention : ''}>
                  {highlightedDisplay}
                </div>
              )}
            />
          </MentionsInput>
        </Form.Item>
        <div style={{ color: 'gray', marginTop: 4, marginLeft: 160 }}>
          {Translation('prospect-referral.field.introductionNote')}
        </div>
        <br />

        <Form.Item name="additionalInformation" label={Translation('prospect-referral.field.additionalInformation')} rules={TINYMCE_REQUIRED_RULES}>
          <Tinymce accessLevel="anonymous" disabled={props.disabled} />
        </Form.Item>
      </div>
      <br />

      {/* Information Setting */}
      <div className={classes.sectionMargin}>
        <div className={classes.divideMargin}>
          <span className={classes.subHeader}>{Translation('prospect-referral.section.informationSetting')}</span>
        </div>

        <Form.List name="tabs">
          {(fields: any, { add, remove }: any) =>
            fields.map((field: any, index: number) => (
              <div key={field.key} className={classes.tabs}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: '70%' }}>
                    <Form.Item
                      name={[field.name, 'tabTitle']}
                      label={intl.formatMessage({ id: 'prospect-referral.field.tabTitle' }, { index: index + 1 })}
                      rules={DEFAULT_REQUIRED_RULES}
                    >
                      <TextField
                        className={classes.borderError}
                        disabled={props.disabled}
                        margin="dense"
                        variant="outlined"
                        fullWidth
                        placeholder={Translation('app.input.placeholder.please-enter')}
                        inputProps={{ maxLength: 40 }}
                      />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'tabDescription']}
                      label={intl.formatMessage({ id: 'prospect-referral.field.tabDescription' }, { index: index + 1 })}
                      rules={TINYMCE_REQUIRED_RULES}
                    >
                      <Tinymce accessLevel="anonymous" disabled={props.disabled} />
                    </Form.Item>
                    <br />
                  </div>
                  {!props.disabled && (
                    <div style={{ display: 'flex', width: 100, justifyContent: 'space-around', marginTop: -10 }}>
                      {fields.length < 2 && (
                        <AddIcon fontSize="large" onClick={() => add()} style={{ cursor: 'pointer' }} />
                      )}
                      {fields.length > 1 && (
                        <RemoveIcon
                          fontSize="large"
                          onClick={() => remove(field.name)}
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          }
        </Form.List>

        <Form.Item name="referralForm" label={Translation('prospect-referral.field.referralForm')} labelTop={true} shouldUpdate>
          <CheckBoxGroupBoolean
            disabled={props.disabled}
            options={ReferralFormOption}
            aria-label="referral form"
          />
        </Form.Item>
      </div>
      <br />

      {/* Lead Distribution Rule */}
      <div className={classes.sectionMargin}>
        <div className={classes.divideMargin}>
          <span className={classes.subHeader}>{Translation('prospect-referral.section.leadDistributionRule')}</span>
        </div>

        <Form.Item
          name={['leadDistributionRule', 'campaignId']}
          label={Translation('prospect-referral.field.mapToCampaignId')}
          rules={[{ required: true, message: MANDATORY_FIELD_ERROR_TEXT }]}
          getValueFromEvent={(e) => e.target.value}
        >
          <Select
            disabled={props.disabled}
            displayEmpty
            variant="outlined"
            style={{ minWidth: 300 }}
          >
            {leadSources.map((source) => (
              <MenuItem key={source._id} value={source.sourceId}>
                {source.sourceName}
              </MenuItem>
            ))}
          </Select>
        </Form.Item>
      </div>
      <br />

      {/* Receiver Setting */}
      <div className={classes.sectionMargin}>
        <div className={classes.divideMargin}>
          <span className={classes.subHeader}>{Translation('prospect-referral.section.receiverSetting')}</span>
        </div>

        <Form.Item
          name="agentSegment"
          label={Translation('prospect-referral.field.segmentForAgent')}
          rules={AGENT_SEGMENT_REQUIRED_RULES}
          labelTop={true}
          shouldUpdate
        >
          <AgentSegmentField
            disabled={props.disabled}
            options={AgentSegmentOptions}
          />
        </Form.Item>

        <Form.Item
          name="status"
          label={Translation('prospect-referral.field.cta')}
          rules={[{ required: true, message: MANDATORY_FIELD_ERROR_TEXT }]}
        >
          <RadioGroup row>
            <FormControlLabel
              value={EPublishStatus.PUBLISHED}
              control={<Radio disabled={props.disabled} />}
              label="Publish"
            />
            <FormControlLabel
              value={EPublishStatus.UNPUBLISHED}
              control={<Radio disabled={props.disabled} />}
              label="Unpublish"
            />
          </RadioGroup>
        </Form.Item>
      </div>

      {/* Footer */}
      <div className={classes.footerContainer}>
        {props.formMode === FormMode.EDIT && (
          <>
            <Button
              variant="contained"
              onClick={() => props.history.push(PROSPECT_REFERRAL_BASE_PATH)}
            >
              {Translation('app.button.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              style={{ marginLeft: 12 }}
              type="submit"
            >
              {Translation('app.button.save')}
            </Button>
          </>
        )}
      </div>
    </Form>
  );
};

export default ProspectReferralDetailForm;
